import React, {FC, useRef} from 'react';
import {Subject} from 'rxjs';
import styled from 'styled-components';

import {ContactsManagerFrame} from '../../libs/contacts-manager/src/lib/ui/web/contactsManagerFrame';
import {KnowledgeBaseFrame} from '../../libs/knowledge-base/web/src/knowledgeBaseFrame';
import {useInitialEffect} from '../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AppContentTypesEnum} from '../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {
  AppPanelId,
  makeAnalyticsPanelId,
  makeAuditTrailPanelId,
  makeCalendarsPanelId,
  makeContactsManagerPanelId,
  makeInboxesPanelId,
  makeKnowledgeBasePanelId,
  makeSequencesPanelId,
} from '../../libs/shared/core/src/store/panels/panelStateBase';
import {RepositionBoundary} from '../../libs/shared/ui/src/components/reposition/repositionBoundary';
import {AnalyticsFrame} from '../analytics/analyticsFrame';
import {AuditTrailFrame} from '../audit_trail/auditTrailFrame';
import {useAppDispatch, useAppSelector} from '../helpers/appReduxHelpers';
import {SingleUseValue} from '../helpers/memoryHelpers';
import {SequencesFrame} from '../sequences/sequencesFrame';
import {fetchApplicationsAsyncAction} from '../store/data/applications/applicationsActions';
import {fetchApplicationsSettingsAsyncAction} from '../store/data/applications/applicationSettingsActions';
import {AppBannersHost} from './app/banners/appBannersHost';
import {BottomBar} from './app/bottomBar/bottomBar';
import {getIsTrialBottomBarVisible} from './app/bottomBar/bottomBarSelectors';
import {TopBar} from './app/topBar/topBar';
import {CalendarsFrame} from './calendars/calendarsFrame';
import {InboxesFrame} from './inboxes/inboxesFrame';
import {LegacyFrameContainer} from './legacy/legacyFrameContainer';
import {LegacyFrameRawBoot} from './legacy/legacyFrameHelpers';
import {LegacyFrameProvider} from './legacy/legacyFrameProvider';

/*
 * Props.
 */

interface AppContentStandardProps {
  panelId: AppPanelId;
  contentType:
    | AppContentTypesEnum.INBOXES
    | AppContentTypesEnum.CALENDARS
    | AppContentTypesEnum.CONTACTS
    | AppContentTypesEnum.ANALYTICS
    | AppContentTypesEnum.KNOWLEDGE_BASE
    | AppContentTypesEnum.SEQUENCES
    | AppContentTypesEnum.AUDIT_TRAIL
    | AppContentTypesEnum.SETTINGS;
  rawBoot$: Subject<LegacyFrameRawBoot> | undefined;
  initialRawBoot: SingleUseValue<unknown> | undefined;
}

/*
 * Style.
 */

const StyledAppContentDiv = styled.div`
  height: 100%;

  display: grid;
  grid-template-areas:
    'top-bar'
    'app-frame'
    'bottom-bar';
  grid-template-rows: auto minmax(300px, 1fr) auto;
`;

const StyledTopBar = styled(TopBar)`
  grid-area: top-bar;
`;

const StandardAppFrameWrapperDiv = styled.div`
  grid-area: app-frame;
  /* Creates a containing block so the individual app frames can be absolutely positioned. */
  position: relative;
  /* Creates a stacking context so we can use z-index to bring the active app to the front. */
  isolation: isolate;
`;

const StyledBottomBar = styled(BottomBar)`
  grid-area: bottom-bar;
`;

const StyledAppBannersHost = styled(AppBannersHost)`
  grid-area: app-frame;
`;

/*
 * Component.
 */

export const AppContentStandard: FC<AppContentStandardProps> = (props) => {
  const {panelId, contentType, rawBoot$, initialRawBoot} = props;
  const appFrameWrapperRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  useInitialEffect(() => {
    dispatch(fetchApplicationsAsyncAction());
    dispatch(fetchApplicationsSettingsAsyncAction());
  });

  const isInboxesVisible = contentType === AppContentTypesEnum.INBOXES;
  const isCalendarsVisible = contentType === AppContentTypesEnum.CALENDARS;
  const isContactsVisible = contentType === AppContentTypesEnum.CONTACTS;
  const isAnalyticsVisible = contentType === AppContentTypesEnum.ANALYTICS;
  const isAuditTrailVisible = contentType === AppContentTypesEnum.AUDIT_TRAIL;
  const isSequencesVisible = contentType === AppContentTypesEnum.SEQUENCES;
  const isKnowledgeBaseVisible = contentType === AppContentTypesEnum.KNOWLEDGE_BASE;
  const isLegacyFrameVisible = !(
    isInboxesVisible ||
    isCalendarsVisible ||
    isContactsVisible ||
    isAnalyticsVisible ||
    isAuditTrailVisible ||
    isSequencesVisible ||
    isKnowledgeBaseVisible
  );

  const isTrialBottomBarVisible = useAppSelector(getIsTrialBottomBarVisible);

  return (
    <StyledAppContentDiv>
      <LegacyFrameProvider>
        <StyledTopBar panelId={panelId} contentType={contentType} />
        <StandardAppFrameWrapperDiv ref={appFrameWrapperRef}>
          <RepositionBoundary boundaryRef={appFrameWrapperRef}>
            <LegacyFrameContainer
              panelId={panelId}
              rawBoot$={rawBoot$ || new Subject()}
              isVisible={isLegacyFrameVisible}
              initialRawBoot={initialRawBoot}
            />
            <CalendarsFrame panelId={makeCalendarsPanelId(panelId)} isVisible={isCalendarsVisible} />
            <AnalyticsFrame panelId={makeAnalyticsPanelId(panelId)} isVisible={isAnalyticsVisible} />
            <ContactsManagerFrame
              panelId={makeContactsManagerPanelId(panelId)}
              isVisible={isContactsVisible}
            />
            <KnowledgeBaseFrame
              panelId={makeKnowledgeBasePanelId(panelId)}
              isVisible={isKnowledgeBaseVisible}
            />
            <AuditTrailFrame panelId={makeAuditTrailPanelId(panelId)} isVisible={isAuditTrailVisible} />
            <SequencesFrame panelId={makeSequencesPanelId(panelId)} isVisible={isSequencesVisible} />
            <InboxesFrame panelId={makeInboxesPanelId(panelId)} isVisible={isInboxesVisible} />
          </RepositionBoundary>
        </StandardAppFrameWrapperDiv>
        <StyledAppBannersHost panelId={panelId} />
        {isTrialBottomBarVisible ? <StyledBottomBar /> : null}
      </LegacyFrameProvider>
    </StyledAppContentDiv>
  );
};
